/* eslint-disable camelcase */

import axios from 'axios'
import qs from 'qs'

const getApi = (options = {}) => {
  const params = {
    paramsSerializer: function (params) {
      return qs.stringify(params, { arrayFormat: 'brackets' })
    },
  }

  const api = axios.create(params)

  api.defaults.timeout = 10000

  return api
}

// --------------------------------------------------
// --------------------------------------------------
// DELETE
// --------------------------------------------------
// --------------------------------------------------

export const deleteEventLegacy = (params = {}) => {
  return axios.delete(`/events/${params.id}.json`)
}

// --------------------------------------------------
// --------------------------------------------------
// GET
// --------------------------------------------------
// --------------------------------------------------

export const getAhsWarrantyContractPricing = ({ contract_id }) => {
  return getApi().get('/api/v1/public/ahs_warranty/get_contract_pricing.json', {
    params: {
      contract_id,
    },
  })
}

export const getAhsWarrantyNormalizedAddress = ({
  address,
  address2 = '',
  city,
  state,
  zip,
}) => {
  return getApi().get(
    '/api/v1/public/ahs_warranty/get_normalized_address.json',
    {
      params: {
        address,
        address2,
        city,
        state,
        zip,
      },
    }
  )
}

export const getAhsWarrantyExistingContract = ({
  address_number,
  address_name,
  address_direction = '',
  state,
  zip,
  unit_number = '',
  unit_type = '',
}) => {
  return getApi().get(
    '/api/v1/public/ahs_warranty/get_existing_contract.json',
    {
      params: {
        address_number,
        address_name,
        address_direction,
        state,
        zip,
        unit_number,
        unit_type,
      },
    }
  )
}

export const getAhsWarrantyProducts = ({
  age,
  contract_term = 1,
  state,
  zip,
  residence_type = '',
  square_footage = '',
}) => {
  return getApi().get('/api/v1/public/ahs_warranty/get_products.json', {
    params: {
      age,
      contract_term,
      state,
      zip,
      residence_type,
      square_footage,
    },
  })
}

export const getAhsWarrantyProduct = ({ product_id, residence_type }) => {
  return getApi().get('/api/v1/public/ahs_warranty/get_product.json', {
    params: {
      product_id,
      residence_type,
    },
  })
}

export const getTags = () => {
  return axios.get('/api/v1/tags')
}

export const getCapacityReport = (params) => {
  return getApi().get('/api/v1/company_reports/capacity_report', { params })
}

export const getContractorRecommendations = () => {
  return getApi().get('/api/v1/contractors')
}

export const getDashboards = (params = {}) => {
  return getApi().get('/api/v1/dashboards.json', { params })
}

export const getEvents = (params = {}) => {
  return getApi().get('/api/v1/events', { params })
}

export const getRecommendations = (params = {}) => {
  return getApi().get('/api/v1/recommendations', { params })
}

// gets all the contractors (along with the quotes that might have already been submitted)
export const getReportContractorRecommendations = ({ report_id }) => {
  return getApi().get('/api/v1/public/report_contractors', {
    params: { report_id },
  })
}

export const getInspections = (params = {}) => {
  return getApi().get('/api/v1/inspections.json', { params })
}

export const getDraftInspections = (params = {}) => {
  return getApi().get('/api/v1/draft_inspections.json', { params })
}

export const getInspection = (params = {}) => {
  return getApi().get(`/api/v1/inspections/${params.id}.json`, { params })
}

export const getInspectionAlerts = (params = {}) => {
  return getApi().get(`/api/v1/inspection_alerts.json`, { params })
}

/**
 * Fetches a list of centralized alerts with optional filters.
 *
 * @function getCentralizedAlerts
 * @param {Object} params - The query parameters to filter alerts.
 * @param {string} [params.showDismissed] - Include dismissed alerts if `true`.
 * @param {string} [params.scope] - Scope of alerts to retrieve, either 'company' or 'inspection'.
 * @param {string} [params.page.size] - Size of the page.
 * @param {string} [params.page.number] - Scope of alerts to retrieve, either 'company' or 'inspection'.
 * @param {Array<string>} [params.inspectionFilters] - Additional inspection filters.
 * @returns {Promise} - A promise that resolves to the list of alerts from the API.
 */
export const getCentralizedAlerts = (params = {}) => {
  const urlParams = {
    'filter[include_dismissed]': params.showDismissed,
    'filter[scope]': params.scope,
    'page[size]': params.page.size,
    'page[number]': params.page.number,
    'sort': params.sortBy === 'desc' ? '-created_at' : 'created_at',
  };

  // Append inspection filters to the query params
  if (params.inspectionFilters?.length) {
    urlParams[`filter[alert_types]`] = params.inspectionFilters;
  };

  return getApi().get(`/api/v2/centralized_alerts.json`, { params: urlParams });
};

/**
 * Dismiss an alert by setting its status to 'dismissed'.
 *
 * @function dismissCentralizedAlert
 * @param {string} alertId - The id of the alert to be dismissed.
 */
export const dismissCentralizedAlert = (alertId) => {
  const dismissState = {
    data: {
      attributes: {
        status: "dismissed"
      }
    }
  };

  return getApi().put(`/api/v2/centralized_alerts/${alertId}.json`, dismissState);
};

/**
 * Restore an alert by setting its status to 'dismissed'.
 *
 * @function restoreCentralizedAlert
 * @param {string} alertId - The id of the alert to be restored.
 */
export const restoreCentralizedAlert = (alertId) => {
  const restoreState = {
    data: {
      attributes: {
        status: "restored"
      }
    }
  };

  return getApi().put(`/api/v2/centralized_alerts/${alertId}.json`, restoreState);
};

/**
 * Update Centralized Alerts user settings.
 *
 * @function updateAlertsSettings
 * @param {object} settings - Settings object to update on the server.
 */
export const updateAlertsSettings = (settings) => {
  return getApi().patch(`/api/v2/users/update_alerts_settings.json`, { settings });
};

export const getInspectionLegacy = (params = {}) => {
  return getApi().get(`/inspections/${params.id}.json`, { params })
}

export const getInspectorIsnPull = (params = {}) => {
  return getApi().get(`/isn_pull_by_inspector/${params.id}.json`, { params })
}

export const getCompanyIsnPull = (params = {}) => {
  return getApi().get(`/isn_pull/${params.id}.json`, { params })
}

export const getInspector = (params = {}) => {
  return getApi().get(`/api/v1/inspectors/${params.id}.json`, {
    params,
  })
}

export const getUserProfile = (params = {}) => {
  return getApi().get('/api/v1/user_profile', { params })
}

export const getTasks = (params = {}) => {
  return getApi().get('/api/v2/tasks', { params })
}

// --------------------------------------------------
// --------------------------------------------------
// POST
// --------------------------------------------------
// --------------------------------------------------

export const postAhsWarrantySendEmail = ({ contract_id, emails = [] }) => {
  return axios.post('/api/v1/public/ahs_warranty/send_email.json', {
    contract_id,
    emails,
  })
}

export const postAhsWarrantyOrder = (params = {}) => {
  // I decided to be explicit with the params here, so we know what's required
  // and what was passed in
  return axios.post('/api/v1/public/ahs_warranty.json', {
    id_token: params.id_token,
    inspection_id: params.inspection_id,
    closing_date: params.closing_date,
    // buyer
    buyer_first_name: params.buyer_first_name,
    buyer_last_name: params.buyer_last_name,
    buyer_phone: params.buyer_phone,
    buyer_email: params.buyer_email,
    // property
    address_name: params.address_name,
    address_number: params.address_number,
    city: params.city,
    state: params.state,
    zip: params.zip,
    residence_type: params.residence_type,
    square_footage: params.square_footage,
    // products
    product_id: params.product_id,
    product_option_ids: params.product_option_ids,
  })
}

export const postEventLegacy = (event = {}) => {
  return axios.post('/events.json', {
    event,
    inspectors: event.inspectorIds || [],
  })
}

export const postContractorLead = (params) => {
  return axios.post('/api/v1/public/contractor_leads', {
    report_view: params.report_view,
    upsell_lead: params.upsell_lead,
  })
}

// --------------------------------------------------
// --------------------------------------------------
// PUT
// --------------------------------------------------
// --------------------------------------------------

export const putCallCenterRepLegacy = (params = {}) => {
  return axios.put(`/call_center_reps/${params.id}.json`, {
    call_center_rep: params,
  })
}

export const putEventLegacy = (params = {}) => {
  return axios.put(`/events/${params.id}.json`, { event: params })
}

export const putInspectorLegacy = (params = {}) => {
  return axios.put(`/inspectors/${params.id}.json`, { inspector: params })
}

export const putInspectionLegacy = (params = {}) => {
  return axios.put(`/inspections/${params.id}.json`, { inspection: params })
}

export const putManagerLegacy = (params = {}) => {
  return axios.put(`/managers/${params.id}.json`, { manager: params })
}

export const putMetricPanel = (params = {}) => {
  return axios.put(`/api/v1/metric_panels/${params.id}.json`, {
    attributes: params,
  })
}

export const putMetricDashboard = (params = {}) => {
  return getApi().put(`/api/v1/metric_dashboards/${params.id}.json`, {
    attributes: params,
  })
}

export const toggleProfileCalendar = () => {
  return axios.patch(`api/v2/profiles/toggle_calendar`, {
    attributes: { calendar_version: `full_calendar` },
  })
}
